import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useChat } from "../hooks/useChat";

// Indicador de "escribiendo…" con tres puntos animados
const TypingDots = () => (
  <div className="flex justify-start">
    <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-white/10 bg-[#12223C] px-4 py-3">
      {["a", "b", "c"].map((k, i) => (
        <motion.span
          key={k}
          className="h-2 w-2 rounded-full bg-yellow-400"
          animate={{ y: [0, -5, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  </div>
);

const MessageList = ({ messages, showTyping }) => {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showTyping]);

  return (
    <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
      {messages.map((m) => (
        <motion.div
          key={m.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
        >
          <div
            className={
              m.role === "user"
                ? "max-w-[80%] rounded-2xl rounded-br-md bg-gradient-to-r from-yellow-400 to-amber-500 px-4 py-2.5 text-sm font-medium text-slate-950"
                : "max-w-[85%] rounded-2xl rounded-bl-md border border-white/10 bg-[#12223C] px-4 py-2.5 text-sm leading-relaxed text-slate-200"
            }
          >
            {m.content}
          </div>
        </motion.div>
      ))}
      {showTyping && <TypingDots />}
      <div ref={bottomRef} />
    </div>
  );
};

const ChatHeader = ({ title, subtitle, onClose }) => (
  <div className="flex items-center gap-3 border-b border-white/10 bg-gradient-to-r from-[#101F38] to-[#16294a] px-4 py-3.5">
    <img src="/images/logo.webp" alt="" className="h-10 w-10 rounded-xl object-cover ring-1 ring-yellow-500/40" />
    <div className="flex-1">
      <div className="font-heading text-sm font-bold text-slate-50">{title}</div>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        {subtitle}
      </div>
    </div>
    <button
      data-testid="chat-close-button"
      onClick={onClose}
      aria-label="Close"
      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-100"
    >
      <X className="h-4 w-4" strokeWidth={2.5} />
    </button>
  </div>
);

const ChatInput = ({ input, setInput, busy, send, placeholder, label }) => (
  <form
    onSubmit={(e) => {
      e.preventDefault();
      send();
    }}
    className="flex items-center gap-2 border-t border-white/10 bg-[#0A1728] p-3"
  >
    <input
      data-testid="chat-input"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      placeholder={placeholder}
      maxLength={500}
      className="flex-1 rounded-xl border border-white/10 bg-[#0B1015]/60 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-yellow-500/50"
    />
    <button
      data-testid="chat-send-button"
      type="submit"
      disabled={busy || !input.trim()}
      aria-label={label}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-950 shadow-lg shadow-yellow-500/25 transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
    >
      <Send className="h-4 w-4" strokeWidth={2.5} />
    </button>
  </form>
);

const Chatbot = () => {
  const { t } = useLanguage();
  const chat = useChat();

  return (
    <>
      {!chat.open && (
        <motion.button
          data-testid="chat-launcher-button"
          onClick={chat.openChat}
          aria-label={t.chat.open}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.92 }}
          className="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-yellow-300 to-amber-600 text-slate-950 shadow-2xl shadow-yellow-500/40"
        >
          <span className="animate-pulse-ring absolute inset-0 rounded-full bg-yellow-400" />
          <Bot className="relative h-7 w-7" strokeWidth={2.2} />
        </motion.button>
      )}

      <AnimatePresence>
        {chat.open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 48, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 48, scale: 0.92 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed bottom-5 right-5 z-50 flex h-[min(68vh,560px)] w-[min(92vw,384px)] flex-col overflow-hidden rounded-3xl border border-yellow-500/30 bg-[#0C1B30]/95 shadow-2xl shadow-black/60 backdrop-blur-xl"
          >
            <ChatHeader title={t.chat.title} subtitle={t.chat.subtitle} onClose={chat.closeChat} />
            <MessageList messages={chat.messages} showTyping={chat.showTyping} />
            {chat.messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-3">
                {t.chat.suggestions.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => chat.send(s)}
                    className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1.5 text-xs font-semibold text-yellow-300 transition-colors duration-200 hover:bg-yellow-500/20"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <ChatInput input={chat.input} setInput={chat.setInput} busy={chat.busy} send={chat.send} placeholder={t.chat.placeholder} label={t.chat.open} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
